import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Download, 
  Plus, 
  BarChart3, 
  TrendingUp, 
  DollarSign,
  Target
} from 'lucide-react'
import Navigation from '../components/Navigation'
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

export default function DemoBacktestPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')


  // Mock回测数据
  const mockBacktests = [
    {
      id: 1,
      strategy_name: '趋势跟踪策略A',
      start_date: '2024-01-01',
      end_date: '2024-06-30',
      total_return: 25.6,
      max_drawdown: -8.2,
      sharpe_ratio: 1.85,
      win_rate: 68.5,
      total_trades: 45,
      status: 'completed'
    },
    {
      id: 2,
      strategy_name: '网格交易策略B',
      start_date: '2024-02-01',
      end_date: '2024-07-31',
      total_return: 18.3,
      max_drawdown: -5.1,
      sharpe_ratio: 1.42,
      win_rate: 72.1,
      total_trades: 89,
      status: 'running'
    },
    {
      id: 3,
      strategy_name: '均值回归策略C',
      start_date: '2024-03-01',
      end_date: '2024-08-31',
      total_return: 12.7,
      max_drawdown: -12.8,
      sharpe_ratio: 0.95,
      win_rate: 55.2,
      total_trades: 67,
      status: 'completed'
    }
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20 text-green-400 border border-green-500/30'
      case 'running':
        return 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return '已完成'
      case 'running':
        return '进行中'
      default:
        return '未知'
    }
  }

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-blue-500/20 rounded-xl border border-blue-500/30">
              <BarChart3 className="w-6 h-6 text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-secondary">总回测数</p>
              <p className="text-2xl font-bold text-gradient">{mockBacktests.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-green-500/20 rounded-xl border border-green-500/30">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-secondary">平均收益率</p>
              <p className="text-2xl font-bold text-gradient">18.9%</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-purple-500/20 rounded-xl border border-purple-500/30">
              <Target className="w-6 h-6 text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-secondary">平均胜率</p>
              <p className="text-2xl font-bold text-gradient">65.3%</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-3 bg-orange-500/20 rounded-xl border border-orange-500/30">
              <DollarSign className="w-6 h-6 text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ocean-secondary">平均夏普比率</p>
              <p className="text-2xl font-bold text-gradient">1.41</p>
            </div>
          </div>
        </div>
      </div>

      {/* 回测列表 */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-ocean-primary">回测记录</h3>
          <div className="flex space-x-3">
            <button className="btn-secondary flex items-center">
              <Download className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">导出报告</span>
            </button>
            <button className="btn-primary flex items-center justify-center" title="新建回测">
              <Plus className="w-4 h-4 mr-2 sm:mr-2" />
              <span className="hidden sm:inline">新建回测</span>
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-4 text-ocean-secondary font-medium">策略名称</th>
                <th className="text-left p-4 text-ocean-secondary font-medium">回测期间</th>
                <th className="text-left p-4 text-ocean-secondary font-medium">总收益率</th>
                <th className="text-left p-4 text-ocean-secondary font-medium">最大回撤</th>
                <th className="text-left p-4 text-ocean-secondary font-medium">夏普比率</th>
                <th className="text-left p-4 text-ocean-secondary font-medium">胜率</th>
                <th className="text-left p-4 text-ocean-secondary font-medium">状态</th>
                <th className="text-left p-4 text-ocean-secondary font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {mockBacktests.map((backtest) => (
                <tr key={backtest.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="p-4">
                    <div className="text-ocean-primary font-medium">{backtest.strategy_name}</div>
                  </td>
                  <td className="p-4">
                    <div className="text-ocean-secondary text-sm">
                      {backtest.start_date} 至 {backtest.end_date}
                    </div>
                  </td>
                  <td className={`p-4 font-semibold ${
                    backtest.total_return > 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {backtest.total_return > 0 ? '+' : ''}{backtest.total_return}%
                  </td>
                  <td className="p-4 text-red-400 font-semibold">
                    {backtest.max_drawdown}%
                  </td>
                  <td className="p-4">
                    <div className="text-ocean-primary font-medium">{backtest.sharpe_ratio}</div>
                  </td>
                  <td className="p-4">
                    <div className="text-ocean-primary font-medium">{backtest.win_rate}%</div>
                  </td>
                  <td className="p-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(backtest.status)}`}>
                      {getStatusLabel(backtest.status)}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <button className="text-violet-300 hover:text-violet-400 p-1">
                        <BarChart3 className="w-4 h-4" />
                      </button>
                      <button className="text-green-300 hover:text-green-400 p-1">
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderAnalysisTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-ocean-primary mb-4">收益分布</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={[
              { range: '-20%以下', count: 2 },
              { range: '-10%到-20%', count: 5 },
              { range: '0%到-10%', count: 8 },
              { range: '0%到10%', count: 12 },
              { range: '10%到20%', count: 15 },
              { range: '20%以上', count: 8 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="range" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip />
              <Bar dataKey="count" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-ocean-primary mb-4">回撤分析</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={[
              { month: '1月', drawdown: -2.1 },
              { month: '2月', drawdown: -1.8 },
              { month: '3月', drawdown: -5.2 },
              { month: '4月', drawdown: -3.7 },
              { month: '5月', drawdown: -8.9 },
              { month: '6月', drawdown: -12.3 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="month" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip />
              <Line type="monotone" dataKey="drawdown" stroke="#ef4444" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-ocean-primary mb-4">策略表现对比</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={[
              { month: '1月', strategyA: 5.2, strategyB: 3.1, benchmark: 2.1 },
              { month: '2月', strategyA: 8.7, strategyB: 6.5, benchmark: 3.5 },
              { month: '3月', strategyA: 12.1, strategyB: 9.8, benchmark: 4.2 },
              { month: '4月', strategyA: 15.8, strategyB: 13.2, benchmark: 5.8 },
              { month: '5月', strategyA: 18.3, strategyB: 16.7, benchmark: 6.9 },
              { month: '6月', strategyA: 20.5, strategyB: 18.9, benchmark: 7.2 },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="month" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="strategyA" stroke="#10b981" strokeWidth={2} name="策略A" />
              <Line type="monotone" dataKey="strategyB" stroke="#3b82f6" strokeWidth={2} name="策略B" />
              <Line type="monotone" dataKey="benchmark" stroke="#6b7280" strokeWidth={2} name="基准" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-ocean-primary mb-4">风险指标分布</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={[
                  { name: '低风险', value: 30, color: '#10b981' },
                  { name: '中风险', value: 45, color: '#f59e0b' },
                  { name: '高风险', value: 25, color: '#ef4444' },
                ]}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {[
                  { name: '低风险', value: 30, color: '#10b981' },
                  { name: '中风险', value: 45, color: '#f59e0b' },
                  { name: '高风险', value: 25, color: '#ef4444' },
                ].map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab()
      case 'analysis':
        return renderAnalysisTab()
      default:
        return renderOverviewTab()
    }
  }

  return (
    <div className="min-h-screen animate-fadein">
      <Navigation />
      
      {/* 主要内容 */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 页面头部 */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => navigate('/demo')}
              className="btn-secondary flex items-center justify-center"
              title="返回主页"
            >
              <ArrowLeft className="w-4 h-4 mr-2 sm:mr-2" />
              <span className="hidden sm:inline">返回主页</span>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gradient">回测分析</h1>
              <p className="text-ocean-secondary">深度分析策略回测结果和表现</p>
            </div>
          </div>
        </div>

        {/* 标签页导航 */}
        <div className="card mb-6">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`pb-2 px-1 border-b-2 font-medium transition-colors ${
                activeTab === 'overview'
                  ? 'border-violet-500 text-ocean-primary'
                  : 'border-transparent text-ocean-secondary hover:text-ocean-primary'
              }`}
            >
              概览
            </button>
            <button
              onClick={() => setActiveTab('analysis')}
              className={`pb-2 px-1 border-b-2 font-medium transition-colors ${
                activeTab === 'analysis'
                  ? 'border-violet-500 text-ocean-primary'
                  : 'border-transparent text-ocean-secondary hover:text-ocean-primary'
              }`}
            >
              深度分析
            </button>
          </div>
        </div>

        {/* 标签页内容 */}
        {renderTabContent()}
      </main>
    </div>
  )
} 